import Phaser from 'phaser';
import BaseScene from './base';
import { makeStandardController, StandardController, wait } from 'tower/util';
import * as C from 'tower/constants';

export interface CreditsOptions {
  context: 'title' | 'ending';
}

export default class CreditsScene extends BaseScene {
  controller!: StandardController;
  cameraBody!: Phaser.Physics.Arcade.Body;
  cameraBounds!: Phaser.Geom.Rectangle;
  options!: CreditsOptions;
  music!: Phaser.Sound.BaseSound;

  stopInput = false;

  constructor(config: Phaser.Types.Scenes.SettingsConfig = {}) {
    super({
      key: 'credits',
      ...config,
    });
  }

  loadResources(scene: Phaser.Scene) {
    scene.load.image('wallBox', 'img/wallbox.png');
    scene.load.audio('ending', 'audio/ending.mp3');
  }

  get isEnding() {
    return this.options.context === 'ending';
  }

  get isTitle() {
    return this.options.context === 'title';
  }

  create(options?: CreditsOptions) {
    super.create();
    this.options = options ?? { context: 'title' };
    this.stopInput = false;

    this.music = this.sound.add('ending').on('complete', () => {
      this.music.play({ seek: 21.299 });
    });

    this.controller = makeStandardController(this);

    const boxOffset = this.isEnding ? 272 : 16;
    if (this.isEnding) {
      this.add.bitmapText(96, 104, 'textWhite', 'FLOOR 2');
    }

    const fonts = ['textBlue', 'textGreen', 'textRed'];
    const lines = [
      { roles: ['PROGRAM', 'DESIGN', 'ART', 'SOUND'], names: ['OSMOSE'] },
      ...(this.isEnding
        ? [
            { roles: ['SQUEAK TOY OWNER'], names: ['BOLT'] },
            { roles: ['SQUEAK RECORDER'], names: ['JAMESPETSDOGS'] },
          ]
        : [{ roles: ['SOUND'], names: ['JAMESPETSDOGS'] }]),
      { roles: ['PLAYTEST'], names: ['ELLIE BELLE', 'YUDOU', 'POTCH', 'GATEKID3', 'SCIMAN'] },
      { roles: ['SPRITE RIPS'], names: ['MISTER MIKE', 'REESE RIVERS', 'JACKSTER'] },
      { roles: ['TITLE FONT'], names: ['CHEVY RAY'] },
      { roles: ['ICONS'], names: ['KENNEY'] },
      { roles: ['SPECIAL THANKS'], names: ['THE LEGEND OF ZELDA\nDEV TEAM', 'THE TOWER OF DRUAGA\nDEV TEAM'] },
    ];

    const boxHeight =
      8 *
      (9 +
        lines.reduce((count: number, line) => {
          return (
            count +
            4 +
            line.roles.length +
            1 +
            line.names.flatMap((n) => n.split('\n').length).reduce((t, l) => t + l + 1, 0) -
            1
          );
        }, 0));

    this.add.nineslice(128, boxOffset + boxHeight / 2, 'wallBox', 0, 224, boxHeight, 24, 32, 24, 32, true, true);
    this.add.bitmapText(48, boxOffset, 'textRed', 'THE TOWER OF ZELDAGA');
    this.add.bitmapText(48, boxOffset + 24, 'textWhite', '   A FAN  DEMO BY\n\nLEGITIMATE  BUSINESS');

    let offset = 80 + boxOffset;
    for (let k = 0; k < lines.length; k++) {
      const { roles, names } = lines[k];
      this.add.bitmapText(48, offset, fonts[k % 3], roles.map((r) => `${r}...`).join('\n'));
      offset += roles.length * 8 + 8;

      const longest = Math.max(...names.flatMap((n) => n.split('\n').map((l) => l.length)));
      this.add.bitmapText(216 - longest * 8, offset, fonts[k % 3], names.join('\n\n'), 8, 2);
      offset += names.flatMap((n) => n.split('\n').length).reduce((t, l) => t + l + 1, 0) * 8 + 24;
    }

    if (this.isEnding) {
      this.add.bitmapText(
        72,
        boxOffset + boxHeight + 104,
        'textWhite',
        'YOU ARE GREAT.\n\nTHANK YOU  FOR\n\n   PLAYING!',
      );
    }

    this.cameraBody = this.physics.add.body(128, 128, 10, 10).setMaxVelocityY(C.CREDITS_MAX_SPEED);
    this.cameras.main.setBounds(0, 0, 256, boxHeight + boxOffset + (this.isEnding ? 256 : 16));
    this.cameras.main.startFollow(this.cameraBody);
    this.cameraBounds = this.cameras.main.getBounds();

    if (this.isEnding) {
      this.stopInput = true;
      (async () => {
        await wait(this, 5000);
        this.stopInput = false;
        this.music.play();
        this.cameraBody.setVelocityY(C.CREDITS_MIN_SPEED);
      })();
    }
  }

  update() {
    const { controller, cameraBody, stopInput } = this;

    if (stopInput) {
      return;
    }

    if (this.isTitle && (controller.button1.justDown || controller.button2.justDown || controller.start.justDown)) {
      this.stopInput = true;
      this.fadeOut().then(() => this.scene.start('title'));
      return;
    }

    if (controller.down.isDown) {
      cameraBody.setAccelerationY(C.CREDITS_ACC);
    } else if (controller.up.isDown) {
      cameraBody.setAccelerationY(-C.CREDITS_ACC);
    } else {
      cameraBody.setAccelerationY(0);
      if (Math.abs(cameraBody.velocity.y) < C.CREDITS_STOP_THRESHOLD) {
        cameraBody.stop();
      } else if (Math.abs(cameraBody.velocity.y) < C.CREDITS_MIN_SPEED) {
        if (cameraBody.velocity.y < 0) {
          cameraBody.setVelocityY(-C.CREDITS_MIN_SPEED);
        } else {
          cameraBody.setVelocityY(C.CREDITS_MIN_SPEED);
        }
      }
    }

    if (cameraBody.y > this.cameraBounds.bottom - 128) {
      cameraBody.y = this.cameraBounds.bottom - 128;
      cameraBody.stop();
    } else if (cameraBody.y < 128) {
      cameraBody.y = 128;
      cameraBody.stop();
    }
  }
}
